<template>
  <div class="kanban-stages-editor">
    <p class="text-muted small mb-2">
      Перетащите этап за ручку <span class="kanban-stages-editor__grip-hint" aria-hidden="true">⋮⋮</span>, чтобы изменить порядок колонок на канбане.
    </p>
    <div
      v-for="(s, idx) in stages"
      :key="stageKey(s, idx)"
      class="kanban-stages-editor__row border rounded p-2 mb-2"
      :class="{ 'kanban-stages-editor__row--drag-over': dragOverIndex === idx && dragFromIndex !== idx }"
      @dragover.prevent="onDragOver(idx)"
      @drop.prevent="onDrop(idx)"
    >
      <div class="row g-2 align-items-end">
        <div class="col-auto">
          <label class="form-label small mb-0 d-block text-center">Порядок</label>
          <button
            type="button"
            class="btn btn-sm btn-light kanban-stages-editor__handle"
            draggable="true"
            aria-label="Перетащить этап"
            @dragstart="onDragStart(idx, $event)"
            @dragend="onDragEnd"
          >
            <i class="fa-solid fa-grip-vertical" aria-hidden="true" />
          </button>
        </div>
        <div class="col-md-6">
          <label class="form-label small mb-0">Название</label>
          <input v-model="s.title" class="form-control form-control-sm" />
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-0">Цвет</label>
          <input v-model="s.color" type="color" class="form-control form-control-color form-control-sm" />
        </div>
        <div class="col-md-2 text-end">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="!canRemoveStage(s)"
            :title="removeStageTitle(s)"
            @click="remove(idx)"
          >
            Удалить
          </button>
          <div v-if="guardTenders && tenderCount(s.id) > 0" class="small text-muted mt-1">
            {{ tenderCount(s.id) }} тенд.
          </div>
        </div>
      </div>
    </div>
    <button v-if="showAdd" type="button" class="btn btn-sm btn-outline-primary" @click="addStage">
      Добавить этап
    </button>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { applyKanbanStageOrder, createKanbanStageId, moveKanbanStage } from '@/utils/kanbanStages'

const props = defineProps({
  stages: { type: Array, required: true },
  /** template — шаблон в кабинете (можно удалить все этапы); list — настройка списка. */
  mode: {
    type: String,
    default: 'list',
    validator: (v) => v === 'template' || v === 'list',
  },
  minCount: { type: Number, default: 1 },
  showAdd: { type: Boolean, default: true },
  /** { [stageId]: number } — сколько тендеров на этапе (только для списка). */
  stageCounts: { type: Object, default: () => ({}) },
  /** true — нельзя удалить этап, пока на нём есть тендеры (настройка списка). */
  guardTenders: { type: Boolean, default: false },
})

const emit = defineEmits(['blocked-remove'])

const dragFromIndex = ref(null)
const dragOverIndex = ref(null)

function stageKey(s, idx) {
  return s.__key || s.id || `stage-${idx}`
}

function onDragStart(idx, event) {
  dragFromIndex.value = idx
  dragOverIndex.value = idx
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(idx))
  }
}

function onDragOver(idx) {
  if (dragFromIndex.value === null) return
  dragOverIndex.value = idx
}

function onDrop(toIdx) {
  const fromIdx = dragFromIndex.value
  if (fromIdx === null || fromIdx === toIdx) return
  moveKanbanStage(props.stages, fromIdx, toIdx)
  onDragEnd()
}

function onDragEnd() {
  dragFromIndex.value = null
  dragOverIndex.value = null
}

function tenderCount(stageId) {
  const id = String(stageId || '').trim()
  return Number(props.stageCounts?.[id] || 0)
}

function isTemplateMode() {
  return props.mode === 'template'
}

function canRemoveStage(stage) {
  if (isTemplateMode()) {
    return props.stages.length > 0
  }
  const min = Math.max(1, Number(props.minCount) || 1)
  if (props.stages.length - 1 < min) return false
  if (props.guardTenders && tenderCount(stage?.id) > 0) return false
  return true
}

function removeStageTitle(stage) {
  if (isTemplateMode()) {
    return props.stages.length > 1
      ? 'Удалить этап из шаблона'
      : 'Удалить последний этап (будут этапы по умолчанию на сервере)'
  }
  const n = tenderCount(stage?.id)
  if (props.guardTenders && n > 0) {
    return `На этапе «${stage?.title || 'этап'}» ${n} тендер(ов). Сначала перенесите их на другой этап.`
  }
  const min = Math.max(1, Number(props.minCount) || 1)
  if (props.stages.length - 1 < min) {
    return 'Нельзя удалить последний этап'
  }
  return 'Удалить этап'
}

function addStage() {
  const n = props.stages.length
  const id = createKanbanStageId()
  props.stages.push({
    __key: `${id}-${Math.random().toString(36).slice(2)}`,
    id,
    title: 'Новый этап',
    color: '#64748b',
    order: n,
  })
  applyKanbanStageOrder(props.stages)
}

function remove(idx) {
  const stage = props.stages[idx]
  if (!canRemoveStage(stage)) {
    emit('blocked-remove', { stage, count: tenderCount(stage?.id) })
    return
  }
  props.stages.splice(idx, 1)
  applyKanbanStageOrder(props.stages)
}
</script>

<style scoped>
.kanban-stages-editor__handle {
  cursor: grab;
  touch-action: none;
  min-width: 2.25rem;
}

.kanban-stages-editor__handle:active {
  cursor: grabbing;
}

.kanban-stages-editor__row--drag-over {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
  background: rgba(13, 110, 253, 0.04);
}

.kanban-stages-editor__grip-hint {
  display: inline-block;
  font-weight: 700;
  letter-spacing: -0.15em;
  color: #94a3b8;
}
</style>
